import * as React from "react";
import {
  format,
  parse,
  isValid,
  eachMonthOfInterval,
  eachYearOfInterval,
  endOfYear,
  startOfYear,
  isBefore,
  isAfter,
} from "date-fns";
import { fr as dateFnsFr } from "date-fns/locale";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { CaptionLabelProps, MonthGridProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";

interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
}

const parseDateValue = (value: string): Date | undefined => {
  if (!value?.trim()) return undefined;
  try {
    const parsed = parse(value, "dd/MM/yyyy", new Date());
    return isValid(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export function DatePicker({
  value = "",
  onChange,
  onBlur,
  placeholder = "jj/mm/aaaa",
  disabled = false,
  className,
  name,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [isYearView, setIsYearView] = React.useState(false);

  const [date, setDate] = React.useState<Date | undefined>(() => parseDateValue(value));
  const [month, setMonth] = React.useState<Date>(() => date || new Date());

  const startDate = new Date(1900, 0);
  const endDate = new Date(2100, 11);
  const years = eachYearOfInterval({
    start: startOfYear(startDate),
    end: endOfYear(endDate),
  });

  React.useEffect(() => {
    const parsed = parseDateValue(value);
    setDate(parsed);
    if (parsed) {
      setMonth(parsed);
    }
  }, [value]);

  const formatDate = (date: Date | undefined): string => {
    if (!date || !isValid(date)) return "";
    return format(date, "dd/MM/yyyy", { locale: dateFnsFr });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange?.(inputValue);

    if (!inputValue.trim()) {
      setDate(undefined);
      return;
    }

    if (inputValue.length === 10) {
      const parsed = parseDateValue(inputValue);
      setDate(parsed);
      if (parsed) {
        setMonth(parsed);
      }
    } else {
      setDate(undefined);
    }
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setDate(selectedDate);
      onChange?.(formatDate(selectedDate));
      setOpen(false);
    }
  };

  const currentYearRef = React.useRef<HTMLDivElement>(null);
  const currentMonthButtonRef = React.useRef<HTMLButtonElement>(null);
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isYearView) return;

    const viewport = scrollAreaRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]"
    ) as HTMLElement | null;

    if (viewport && currentYearRef.current) {
      viewport.scrollTop = currentYearRef.current.offsetTop;
      setTimeout(() => currentMonthButtonRef.current?.focus(), 100);
    }
  }, [isYearView]);

  return (
    <div className="relative">
      <Input
        type="text"
        name={name}
        value={value}
        onChange={handleInputChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "h-[48px] px-[14px] py-[10px] pl-[42px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288] w-full",
          className
        )}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !disabled) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="absolute left-[14px] top-1/2 -translate-y-1/2 h-5 w-5 p-0 hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center z-0"
            aria-label="Select date"
          >
            <CalendarIcon className="h-5 w-5 text-[#757288]" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" sideOffset={5}>
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            month={month}
            onMonthChange={setMonth}
            defaultMonth={new Date()}
            startMonth={startDate}
            endMonth={endDate}
            className="overflow-hidden rounded-lg border border-border p-2 bg-background"
            classNames={{
              month_caption: "ms-2.5 me-20 justify-start",
              nav: "justify-end",
            }}
            components={{
              CaptionLabel: (props: CaptionLabelProps) => (
                <CaptionLabel isYearView={isYearView} setIsYearView={setIsYearView} {...props} />
              ),
              MonthGrid: (props: MonthGridProps) => {
                return (
                  <MonthGrid
                    className={props.className}
                    isYearView={isYearView}
                    startDate={startDate}
                    endDate={endDate}
                    years={years}
                    currentYear={month.getFullYear()}
                    currentMonth={month.getMonth()}
                    onMonthSelect={(selectedMonth: Date) => {
                      setMonth(selectedMonth);
                      setIsYearView(false);
                    }}
                    scrollAreaRef={scrollAreaRef}
                    currentYearRef={currentYearRef}
                    currentMonthButtonRef={currentMonthButtonRef}
                  >
                    {props.children}
                  </MonthGrid>
                );
              },
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function CaptionLabel({
  children,
  isYearView,
  setIsYearView,
}: {
  isYearView: boolean;
  setIsYearView: React.Dispatch<React.SetStateAction<boolean>>;
} & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <Button
      className="-ms-2 flex items-center gap-2 text-sm font-medium hover:bg-transparent data-[state=open]:text-muted-foreground/80 [&[data-state=open]>svg]:rotate-180"
      variant="ghost"
      size="sm"
      onClick={() => setIsYearView((prev) => !prev)}
      data-state={isYearView ? "open" : "closed"}
    >
      {children}
      <ChevronDown
        size={16}
        strokeWidth={2}
        className="shrink-0 text-muted-foreground/80 transition-transform duration-200"
        aria-hidden="true"
      />
    </Button>
  );
}

function MonthGrid({
  className,
  children,
  isYearView,
  startDate,
  endDate,
  years,
  currentYear,
  currentMonth,
  onMonthSelect,
  scrollAreaRef,
  currentYearRef,
  currentMonthButtonRef,
}: {
  className?: string;
  children: React.ReactNode;
  isYearView: boolean;
  startDate: Date;
  endDate: Date;
  years: Date[];
  currentYear: number;
  currentMonth: number;
  onMonthSelect: (date: Date) => void;
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  currentYearRef: React.RefObject<HTMLDivElement>;
  currentMonthButtonRef: React.RefObject<HTMLButtonElement>;
}) {
  return (
    <div className="relative">
      <table className={className}>{children}</table>
      {isYearView && (
        <div className="absolute inset-0 z-20 -mx-2 -mb-2 bg-background">
          <ScrollArea ref={scrollAreaRef} className="h-full">
            {years.map((year) => {
              const yearNum = year.getFullYear();
              const months = eachMonthOfInterval({
                start: startOfYear(year),
                end: endOfYear(year),
              });
              const isCurrentYear = yearNum === currentYear;

              return (
                <div key={yearNum} ref={isCurrentYear ? currentYearRef : undefined}>
                  <CollapsibleYear title={yearNum.toString()} open={isCurrentYear}>
                    <div className="grid grid-cols-3 gap-2">
                      {months.map((month) => {
                        const isDisabled = isBefore(month, startDate) || isAfter(month, endDate);
                        const isCurrentMonth =
                          month.getMonth() === currentMonth && yearNum === currentYear;

                        return (
                          <Button
                            key={month.getTime()}
                            ref={isCurrentMonth ? currentMonthButtonRef : undefined}
                            variant={isCurrentMonth ? "default" : "outline"}
                            size="sm"
                            className="h-7"
                            disabled={isDisabled}
                            onClick={() => onMonthSelect(month)}
                          >
                            {format(month, "MMM", { locale: dateFnsFr })}
                          </Button>
                        );
                      })}
                    </div>
                  </CollapsibleYear>
                </div>
              );
            })}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

function CollapsibleYear({
  title,
  children,
  open,
}: {
  title: string;
  children: React.ReactNode;
  open?: boolean;
}) {
  return (
    <Collapsible className="border-t border-border px-2 py-1.5" defaultOpen={open}>
      <CollapsibleTrigger asChild>
        <Button
          className="flex w-full justify-start gap-2 text-sm font-medium hover:bg-transparent [&[data-state=open]>svg]:rotate-180"
          variant="ghost"
          size="sm"
        >
          <ChevronDown
            size={16}
            strokeWidth={2}
            className="shrink-0 text-muted-foreground/80 transition-transform duration-200"
            aria-hidden="true"
          />
          {title}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden px-3 py-1 text-sm transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
